import { useEffect, useState } from "react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/**
 * Sélecteur de compte adapté mobile : sur écran tactile, on utilise le
 * `<select>` natif HTML pour bénéficier du picker iOS/Android (defilement +
 * accessibilité). Sur desktop, on garde le composant shadcn Select.
 *
 * Props :
 *   - value : string
 *   - onValueChange : (value: string) => void
 *   - options : Array<{ value: string, label: string }>  // mode liste plate
 *   - groups  : Array<{ title: string, options: [...] }>  // mode groupes (recommandés + tous)
 *     Si `groups` est fourni, il prend le pas sur `options`.
 *   - placeholder, disabled, "data-testid"
 *   - className : appliqué au wrapper visuel
 */
export default function CompteSelect({
  value,
  onValueChange,
  options,
  groups,
  placeholder = "Choisir…",
  disabled = false,
  className = "",
  ...rest
}) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 640px)").matches : false
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 640px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const testid = rest["data-testid"];
  const opts = options || [];
  const grps = groups || [];
  const useGroups = grps.length > 0;

  if (isMobile) {
    return (
      <select
        value={value || ""}
        onChange={(e) => onValueChange(e.target.value)}
        disabled={disabled}
        data-testid={testid}
        className={`w-full h-10 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#52a675] focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      >
        {!value && <option value="">{placeholder}</option>}
        {useGroups
          ? grps.map((g) => (
              <optgroup key={g.title} label={g.title}>
                {g.options.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </optgroup>
            ))
          : opts.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
      </select>
    );
  }

  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger className={className} data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-[400px]">
        {useGroups
          ? grps.map((g, idx) => (
              <div key={g.title}>
                {idx > 0 && <div className="h-px bg-gray-200 my-1" />}
                <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-gray-400 font-semibold sticky top-0 bg-white">
                  {g.title}
                </div>
                {g.options.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </div>
            ))
          : opts.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
      </SelectContent>
    </Select>
  );
}
